import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban,
  Check,
  CheckCircle2,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileIcon,
  ListTodo,
  Loader2,
  Lock,
  Paperclip,
  Play,
  Send,
  TabletSmartphone,
  Trash2,
  Undo2,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { formatDateOnly, formatDateTime, formatRelative } from '@/lib/formatters';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Label } from '@/components/ui/Label';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import { DatePicker } from '@/components/ui/DatePicker';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { initialsOf, PeoplePicker, type PeoplePickerProfile } from '@/components/projects/PeoplePicker';
import { MentionTextarea } from '@/components/projects/MentionTextarea';
import { useProfiles } from '@/features/users/hooks';
import { useProjects } from '@/features/projects/hooks';
import {
  useCreateTaskFromTicket,
  useCreateTicketComment,
  useDeleteTicket,
  useDeleteTicketAttachment,
  useDeleteTicketComment,
  useSetTicketWatchers,
  useTicket,
  useTicketAttachments,
  useTicketCategories,
  useTicketComments,
  useTicketEvents,
  useTicketSignedUrl,
  useTicketWatchers,
  useUpdateTicket,
  useUploadTicketAttachment,
  type TicketAttachmentWithUploader,
  type TicketCommentWithAuthor,
  type TicketEventWithActor,
  type TicketPerson,
  type TicketWithContext,
  type UpdateTicketInput,
} from '@/features/tickets/hooks';
import {
  HANDLER_STATUS_OPTIONS,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_META,
  TICKET_STATUS_META,
  describeTicketError,
  describeTicketEvent,
  isTicketClosed,
  personLabel,
  ticketRef,
} from '@/features/tickets/model';
import type { TicketPriority, TicketStatus } from '@/types/database';
import {
  PersonChip,
  TicketCategoryLabel,
  TicketPriorityChip,
  TicketStatusChip,
} from './TicketChips';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

interface TicketDetailDrawerProps {
  ticketId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Everything about one ticket, in a right-hand sheet (full-screen on
 * phones). Who may change what mirrors the DB trigger:
 *   * triage tier / assignee — status, routing, resolution, task-list link,
 *     and every request field
 *   * requester — the request fields (title, details, category,
 *     priority, needed by) and "Cancel request"
 *   * everyone who can see it — comments, attachments, follow / unfollow
 */
export function TicketDetailDrawer({ ticketId, open, onOpenChange }: TicketDetailDrawerProps) {
  const ticketQ = useTicket(ticketId);
  const [last, setLast] = useState<TicketWithContext | null>(null);
  useEffect(() => {
    if (ticketQ.data) setLast(ticketQ.data);
  }, [ticketQ.data]);
  const ticket = ticketQ.data ?? (ticketId && last?.id === ticketId ? last : null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        {ticket ? (
          <TicketDetailContent key={ticket.id} ticket={ticket} onClose={() => onOpenChange(false)} />
        ) : ticketQ.isLoading ? (
          <>
            <SheetHeader>
              <SheetTitle>Loading…</SheetTitle>
              <SheetDescription className="sr-only">Loading ticket</SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </SheetBody>
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>Ticket not found</SheetTitle>
              <SheetDescription>
                It may have been deleted, or you no longer have access to it.
              </SheetDescription>
            </SheetHeader>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function TicketDetailContent({
  ticket,
  onClose,
}: {
  ticket: TicketWithContext;
  onClose: () => void;
}) {
  const { profile, role } = useAuth();
  const me = profile?.id ?? null;
  const canTriage = role === 'super_admin' || role === 'admin' || role === 'manager';
  const isAssignee = !!me && ticket.assignee_id === me;
  const isRequester = !!me && ticket.requester_id === me;
  const canWork = canTriage || isAssignee;
  const closed = isTicketClosed(ticket.status);
  const canEditRequest = (canWork || isRequester) && !closed;

  const update = useUpdateTicket();
  const del = useDeleteTicket();
  const profilesQ = useProfiles();
  const people: PeoplePickerProfile[] = useMemo(
    () =>
      (profilesQ.data ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({ id: p.id, full_name: p.full_name, email: p.email, avatar_url: p.avatar_url })),
    [profilesQ.data],
  );

  const persist = (patch: UpdateTicketInput['patch'], onDone?: () => void) => {
    update.mutate(
      { id: ticket.id, patch },
      {
        onError: (err) => toast.error(describeTicketError(err)),
        onSuccess: onDone,
      },
    );
  };

  const [resolveFor, setResolveFor] = useState<'done' | 'declined' | null>(null);

  const setStatus = (next: TicketStatus) => {
    if (next === ticket.status) return;
    if (next === 'done' || next === 'declined') {
      setResolveFor(next);
      return;
    }
    persist({ status: next });
  };

  const onDelete = () => {
    if (!window.confirm(`Delete ${ticketRef(ticket.number)} for good? Cancelling is usually enough.`)) return;
    del.mutate(ticket.id, {
      onError: (err) => toast.error(describeTicketError(err)),
      onSuccess: () => {
        toast.success('Ticket deleted.');
        onClose();
      },
    });
  };

  return (
    <>
      <SheetHeader className="gap-2 pr-10">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono">{ticketRef(ticket.number)}</span>
          {ticket.source === 'work_mode' && (
            <Badge variant="info-soft" className="gap-1 rounded-full">
              <TabletSmartphone className="h-3 w-3" /> From the floor
            </Badge>
          )}
          <TicketCategoryLabel category={ticket.category} className="text-xs text-muted-foreground" />
        </div>
        <TitleEditor
          value={ticket.title}
          disabled={!canEditRequest}
          onCommit={(title) => persist({ title })}
        />
        <SheetTitle className="sr-only">{ticket.title}</SheetTitle>
        <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span>
            Requested by{' '}
            <span className="text-foreground">{personLabel(ticket.requester, 'a former teammate')}</span>
            {ticket.creator && ticket.created_by !== ticket.requester_id && (
              <> (filed by {personLabel(ticket.creator)})</>
            )}{' '}
            <span title={formatDateTime(ticket.created_at)}>{formatRelative(ticket.created_at)}</span>
          </span>
          {ticket.workstation && <span>· Station: {ticket.workstation.name}</span>}
          {ticket.item && <span>· Item: {ticket.item.name}</span>}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        <ActionBar
          ticket={ticket}
          canWork={canWork}
          isRequester={isRequester}
          closed={closed}
          pending={update.isPending}
          onStatus={setStatus}
          onCancel={() => persist({ status: 'cancelled' })}
          onReopen={() =>
            canWork
              ? persist({ status: 'acknowledged', resolution: null })
              : persist({ status: ticket.assignee_id ? 'acknowledged' : 'new' })
          }
          onTakeIt={() => persist({ assignee_id: me })}
        />

        <FieldGrid
          ticket={ticket}
          people={people}
          canWork={canWork}
          canTriage={canTriage}
          canEditRequest={canEditRequest}
          isRequester={isRequester}
          closed={closed}
          onPersist={persist}
          onStatus={setStatus}
        />

        <DescriptionEditor
          value={ticket.description ?? ''}
          disabled={!canEditRequest}
          onCommit={(description) => persist({ description })}
        />

        {(closed || ticket.resolution) && (
          <ResolutionBlock
            ticket={ticket}
            canWork={canWork && ticket.status !== 'cancelled'}
            onCommit={(resolution) => persist({ resolution })}
          />
        )}

        <LinkedTaskSection ticket={ticket} canWork={canWork} />

        <AttachmentsSection ticket={ticket} canUpload={!closed} canWork={canWork} />

        <ActivitySection ticket={ticket} people={people} canWork={canWork} closed={closed} />

        {role === 'super_admin' && (
          <div className="flex justify-end border-t border-border pt-4">
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" /> Delete ticket
            </Button>
          </div>
        )}
      </SheetBody>

      <ResolveDialog
        open={resolveFor !== null}
        kind={resolveFor ?? 'done'}
        ticket={ticket}
        pending={update.isPending}
        onClose={() => setResolveFor(null)}
        onConfirm={(resolution) =>
          persist({ status: resolveFor ?? 'done', resolution }, () => setResolveFor(null))
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Title / description editors — autosave on blur, Enter / Cmd+Enter
// ---------------------------------------------------------------------------

function TitleEditor({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const next = draft.trim();
    if (!next) {
      setDraft(value);
      return;
    }
    if (next !== value) onCommit(next);
  };
  if (disabled) {
    return <h2 className="text-lg font-medium leading-snug text-foreground">{value}</h2>;
  }
  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') setDraft(value);
      }}
      maxLength={160}
      aria-label="Ticket title"
      className="h-auto border-transparent bg-transparent px-0 text-lg font-medium leading-snug shadow-none hover:border-border focus-visible:border-input focus-visible:px-2"
    />
  );
}

function DescriptionEditor({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const next = draft.trim();
    if (next !== value.trim()) onCommit(next || null);
  };
  return (
    <section className="space-y-2">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Details</h3>
      {disabled ? (
        value ? (
          <p className="whitespace-pre-wrap text-sm text-foreground">{value}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No details given.</p>
        )
      ) : (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') (e.target as HTMLTextAreaElement).blur();
          }}
          placeholder="Anything that helps: where, why, which part, how many, links…"
          className="min-h-[80px]"
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Action bar — the one-tap moves for whoever is looking
// ---------------------------------------------------------------------------

function ActionBar({
  ticket,
  canWork,
  isRequester,
  closed,
  pending,
  onStatus,
  onCancel,
  onReopen,
  onTakeIt,
}: {
  ticket: TicketWithContext;
  canWork: boolean;
  isRequester: boolean;
  closed: boolean;
  pending: boolean;
  onStatus: (s: TicketStatus) => void;
  onCancel: () => void;
  onReopen: () => void;
  onTakeIt: () => void;
}) {
  const { profile } = useAuth();
  const buttons: React.ReactNode[] = [];

  if (closed) {
    if (canWork || (isRequester && ticket.status === 'cancelled')) {
      buttons.push(
        <Button key="reopen" size="sm" variant="outline" onClick={onReopen} disabled={pending}>
          <Undo2 className="h-3.5 w-3.5" /> Reopen
        </Button>,
      );
    }
  } else if (canWork) {
    if (!ticket.assignee_id || ticket.assignee_id !== profile?.id) {
      buttons.push(
        <Button key="take" size="sm" variant="outline" onClick={onTakeIt} disabled={pending}>
          <UserPlus className="h-3.5 w-3.5" /> {ticket.assignee_id ? 'Take it over' : 'Take it'}
        </Button>,
      );
    }
    if (ticket.status === 'new' || ticket.status === 'acknowledged' || ticket.status === 'waiting') {
      buttons.push(
        <Button key="start" size="sm" onClick={() => onStatus('in_progress')} disabled={pending}>
          <Play className="h-3.5 w-3.5" /> Start
        </Button>,
      );
    }
    if (ticket.status === 'in_progress' || ticket.status === 'on_order' || ticket.status === 'waiting') {
      buttons.push(
        <Button key="done" size="sm" onClick={() => onStatus('done')} disabled={pending}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Mark done
        </Button>,
      );
    }
    buttons.push(
      <Button
        key="decline"
        size="sm"
        variant="ghost"
        className="text-muted-foreground"
        onClick={() => onStatus('declined')}
        disabled={pending}
      >
        <Ban className="h-3.5 w-3.5" /> Decline
      </Button>,
    );
  }
  if (!closed && isRequester && !canWork) {
    buttons.push(
      <Button key="cancel" size="sm" variant="ghost" className="text-muted-foreground" onClick={onCancel} disabled={pending}>
        <Ban className="h-3.5 w-3.5" /> Cancel request
      </Button>,
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2">
      <TicketStatusChip status={ticket.status} />
      <span className="text-xs text-muted-foreground">{TICKET_STATUS_META[ticket.status].hint}</span>
      {buttons.length > 0 && <div className="ml-auto flex flex-wrap items-center gap-2">{buttons}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field grid
// ---------------------------------------------------------------------------

function FieldGrid({
  ticket,
  people,
  canWork,
  canTriage,
  canEditRequest,
  isRequester,
  closed,
  onPersist,
  onStatus,
}: {
  ticket: TicketWithContext;
  people: PeoplePickerProfile[];
  canWork: boolean;
  canTriage: boolean;
  canEditRequest: boolean;
  isRequester: boolean;
  closed: boolean;
  onPersist: (patch: UpdateTicketInput['patch']) => void;
  onStatus: (s: TicketStatus) => void;
}) {
  const categoriesQ = useTicketCategories({ includeInactive: true });
  const categories = (categoriesQ.data ?? []).filter((c) => c.is_active || c.id === ticket.category_id);
  const UNASSIGNED = '__none__';

  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-3 text-sm sm:grid-cols-[120px_minmax(0,1fr)] sm:gap-x-4">
      <Field label="Status">
        {canWork && !closed ? (
          <Select value={ticket.status} onValueChange={(v) => onStatus(v as TicketStatus)}>
            <SelectTrigger className="h-9 w-full">
              <span>
                <TicketStatusChip status={ticket.status} />
              </span>
            </SelectTrigger>
            <SelectContent>
              {(ticket.status === 'new' ? ['new', ...HANDLER_STATUS_OPTIONS] : HANDLER_STATUS_OPTIONS).map((s) => (
                <SelectItem key={s} value={s}>
                  <span className="flex flex-col">
                    <span>{TICKET_STATUS_META[s as TicketStatus].label}</span>
                    <span className="text-[11px] text-muted-foreground">{TICKET_STATUS_META[s as TicketStatus].hint}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex h-9 items-center">
            <TicketStatusChip status={ticket.status} />
          </div>
        )}
      </Field>

      <Field label="Handled by">
        {canWork && !closed ? (
          <Select
            value={ticket.assignee_id ?? UNASSIGNED}
            onValueChange={(v) => onPersist({ assignee_id: v === UNASSIGNED ? null : v })}
          >
            <SelectTrigger className="h-9 w-full">
              <span className="flex min-w-0 items-center">
                <PersonChip person={ticket.assignee} fallback="Nobody yet — route it" />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Nobody (unrouted)</SelectItem>
              {people.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.full_name ?? p.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex h-9 items-center">
            <PersonChip person={ticket.assignee} fallback="Nobody yet" />
            {ticket.auto_routed && ticket.assignee && (
              <span className="ml-2 text-xs text-muted-foreground">(auto)</span>
            )}
          </div>
        )}
      </Field>

      <Field label="Priority">
        {canEditRequest ? (
          <Select value={ticket.priority} onValueChange={(v) => onPersist({ priority: v as TicketPriority })}>
            <SelectTrigger className="h-9 w-full">
              <span>
                <TicketPriorityChip priority={ticket.priority} />
              </span>
            </SelectTrigger>
            <SelectContent>
              {TICKET_PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {TICKET_PRIORITY_META[p].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex h-9 items-center">
            <TicketPriorityChip priority={ticket.priority} />
          </div>
        )}
      </Field>

      <Field label="Category">
        {canEditRequest ? (
          <Select
            value={ticket.category_id ?? UNASSIGNED}
            onValueChange={(v) => onPersist({ category_id: v === UNASSIGNED ? null : v })}
          >
            <SelectTrigger className="h-9 w-full">
              <span className="flex min-w-0 items-center">
                <TicketCategoryLabel category={ticket.category} />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Uncategorised</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex h-9 items-center">
            <TicketCategoryLabel category={ticket.category} />
          </div>
        )}
      </Field>

      <Field label="Needed by">
        {canEditRequest ? (
          <DatePicker
            value={ticket.needed_by ?? ''}
            onChange={(next) => onPersist({ needed_by: next || null })}
            placeholder="No date"
            aria-label="Needed by"
            className="h-9"
          />
        ) : (
          <div className="flex h-9 items-center text-sm">
            {ticket.needed_by ? formatDateOnly(ticket.needed_by) : <span className="text-muted-foreground">No date</span>}
          </div>
        )}
      </Field>

      <Field label="Requester">
        {canTriage && !closed ? (
          <Select
            value={ticket.requester_id ?? UNASSIGNED}
            onValueChange={(v) => v !== UNASSIGNED && onPersist({ requester_id: v })}
          >
            <SelectTrigger className="h-9 w-full">
              <span className="flex min-w-0 items-center">
                <PersonChip person={ticket.requester} fallback="Former teammate" />
              </span>
            </SelectTrigger>
            <SelectContent>
              {people.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.full_name ?? p.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex h-9 items-center">
            <PersonChip person={ticket.requester} fallback="Former teammate" />
          </div>
        )}
      </Field>

      <Field label="Followers">
        <FollowersField ticket={ticket} people={people} canManage={canWork || isRequester} />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <div className="flex h-9 items-center text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </>
  );
}

function FollowersField({
  ticket,
  people,
  canManage,
}: {
  ticket: TicketWithContext;
  people: PeoplePickerProfile[];
  canManage: boolean;
}) {
  const { profile } = useAuth();
  const watchersQ = useTicketWatchers(ticket.id);
  const setWatchers = useSetTicketWatchers();
  const ids = (watchersQ.data ?? []).map((w) => w.profile_id);
  const me = profile?.id;
  const following = !!me && ids.includes(me);
  const implicit = new Set([ticket.requester_id, ticket.assignee_id].filter(Boolean) as string[]);

  const onChange = (next: string[]) => {
    const add = next.filter((id) => !ids.includes(id));
    const remove = ids.filter((id) => !next.includes(id));
    if (add.length === 0 && remove.length === 0) return;
    setWatchers.mutate(
      { ticketId: ticket.id, add, remove },
      { onError: (err) => toast.error(describeTicketError(err)) },
    );
  };

  if (canManage) {
    return (
      <PeoplePicker
        profiles={people.filter((p) => !implicit.has(p.id))}
        value={ids}
        onChange={onChange}
        placeholder="Add people who should follow along…"
      />
    );
  }
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-2">
      {ids.length === 0 && <span className="text-sm text-muted-foreground">Requester and handler only.</span>}
      {(watchersQ.data ?? []).map((w) => (
        <PersonChip key={w.profile_id} person={w.profile} size="xs" />
      ))}
      {me && !implicit.has(me) && (
        <Button
          size="sm"
          variant="outline"
          className="h-7"
          onClick={() =>
            setWatchers.mutate(
              { ticketId: ticket.id, add: following ? [] : [me], remove: following ? [me] : [] },
              { onError: (err) => toast.error(describeTicketError(err)) },
            )
          }
          disabled={setWatchers.isPending}
        >
          {following ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {following ? 'Unfollow' : 'Follow'}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resolution — what was done / why it was declined
// ---------------------------------------------------------------------------

function ResolutionBlock({
  ticket,
  canWork,
  onCommit,
}: {
  ticket: TicketWithContext;
  canWork: boolean;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(ticket.resolution ?? '');
  useEffect(() => setDraft(ticket.resolution ?? ''), [ticket.resolution]);
  const title =
    ticket.status === 'declined' ? 'Why it was declined' : ticket.status === 'done' ? 'What was done' : 'Resolution';
  return (
    <section className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground">{title}</h3>
      {canWork ? (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const next = draft.trim();
            if (next !== (ticket.resolution ?? '')) onCommit(next || null);
          }}
          placeholder="A line for the requester."
          className="min-h-[64px] bg-card"
        />
      ) : (
        <p className="whitespace-pre-wrap text-sm text-foreground">
          {ticket.resolution ?? <span className="text-muted-foreground">No note left.</span>}
        </p>
      )}
      {ticket.resolved_at && (
        <p className="text-xs text-muted-foreground">
          Closed {formatRelative(ticket.resolved_at)}
        </p>
      )}
    </section>
  );
}

function ResolveDialog({
  open,
  kind,
  ticket,
  pending,
  onClose,
  onConfirm,
}: {
  open: boolean;
  kind: 'done' | 'declined';
  ticket: TicketWithContext;
  pending: boolean;
  onClose: () => void;
  onConfirm: (resolution: string | null) => void;
}) {
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) setNote(ticket.resolution ?? '');
  }, [open, ticket.resolution]);
  const requester = personLabel(ticket.requester, 'the requester');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{kind === 'done' ? 'Mark as done' : 'Decline this request'}</DialogTitle>
          <DialogDescription>
            {kind === 'done'
              ? `${requester} will be told it is done. Leave a line about what happened.`
              : `${requester} will see the reason. Declining is fine — a ticket is a request, not an order.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="ticket-resolution">{kind === 'done' ? 'What was done' : 'Why not'}</Label>
          <Textarea
            id="ticket-resolution"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={kind === 'done' ? 'Ordered two, they are in the drawer by the CNC.' : 'We have three spares in the cabinet — grab one of those.'}
            autoFocus
            className="min-h-[88px]"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Back
          </Button>
          <Button
            variant={kind === 'declined' ? 'destructive' : 'default'}
            onClick={() => onConfirm(note.trim() || null)}
            disabled={pending || (kind === 'declined' && note.trim().length === 0)}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : kind === 'done' ? <Check className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
            {kind === 'done' ? 'Done' : 'Decline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Linked task (in a task list under My tasks)
// ---------------------------------------------------------------------------

function LinkedTaskSection({ ticket, canWork }: { ticket: TicketWithContext; canWork: boolean }) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const projectsQ = useProjects();
  const createTask = useCreateTaskFromTicket();
  const update = useUpdateTicket();
  const projects = (projectsQ.data ?? []).filter((p) => !p.archived_at);

  const linked = ticket.linked_task;

  const onCreate = () => {
    if (!projectId) return;
    createTask.mutate(
      { ticket, projectId },
      {
        onSuccess: () => {
          toast.success('Added to your task list.');
          setOpen(false);
        },
        onError: (err) => toast.error(describeTicketError(err)),
      },
    );
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Task list</h3>
        {canWork && !linked && !isTicketClosed(ticket.status) && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <ListTodo className="h-3.5 w-3.5" /> Add to a task list
          </Button>
        )}
      </div>
      {linked ? (
        <div className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
          <ListTodo className="h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <Link
              to={`/projects/${linked.project_id}?task=${linked.id}`}
              className={cn('block truncate text-sm font-medium text-foreground hover:underline', linked.is_completed && 'line-through text-muted-foreground')}
            >
              {linked.name}
            </Link>
            <p className="text-xs text-muted-foreground">
              {linked.is_completed ? 'Completed in its project' : 'Open in its project'}
            </p>
          </div>
          <Link
            to={`/projects/${linked.project_id}?task=${linked.id}`}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Open task"
          >
            <ExternalLink className="h-4 w-4" />
          </Link>
          {canWork && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-muted-foreground"
              onClick={() =>
                update.mutate(
                  { id: ticket.id, patch: { linked_task_id: null } },
                  { onError: (err) => toast.error(describeTicketError(err)) },
                )
              }
            >
              Unlink
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {canWork
            ? 'Accepted it? Put it on one of your task lists so it sits with the rest of your work. The ticket stays linked.'
            : 'The person handling this can add it to their own task list.'}
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add to a task list</DialogTitle>
            <DialogDescription>
              Creates a task named “{ticketRef(ticket.number)} {ticket.title}” in the project you pick and links
              it back here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder={projectsQ.isLoading ? 'Loading…' : 'Pick a project'} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!projectsQ.isLoading && projects.length === 0 && (
              <p className="text-xs text-muted-foreground">
                You have no projects yet — create one under Tasks → My tasks first.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onCreate} disabled={!projectId || createTask.isPending}>
              {createTask.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Add task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

function AttachmentsSection({
  ticket,
  canUpload,
  canWork,
}: {
  ticket: TicketWithContext;
  canUpload: boolean;
  canWork: boolean;
}) {
  const attachmentsQ = useTicketAttachments(ticket.id);
  const upload = useUploadTicketAttachment();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const attachments = attachmentsQ.data ?? [];

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast.error(`"${file.name}" is over the 25 MB limit.`);
          continue;
        }
        await upload.mutateAsync({ ticketId: ticket.id, file });
      }
    } catch (err) {
      toast.error(describeTicketError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">
          Attachments{attachments.length > 0 && <span className="ml-1 text-foreground/80">({attachments.length})</span>}
        </h3>
        {canUpload && (
          <>
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              {uploading ? 'Uploading…' : 'Add file'}
            </Button>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => void handleFiles(e.target.files)} />
          </>
        )}
      </div>
      {attachmentsQ.isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : attachments.length === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No attachments yet.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {attachments.map((a) => (
            <AttachmentCard key={a.id} attachment={a} canWork={canWork} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AttachmentCard({ attachment, canWork }: { attachment: TicketAttachmentWithUploader; canWork: boolean }) {
  const { profile } = useAuth();
  const urlQ = useTicketSignedUrl(attachment.bucket, attachment.storage_path);
  const remove = useDeleteTicketAttachment();
  const isImage = (attachment.mime_type ?? '').startsWith('image/');
  const canDelete = canWork || attachment.uploaded_by === profile?.id;
  return (
    <li className="group relative overflow-hidden rounded-md border border-border bg-background">
      {isImage ? (
        urlQ.data ? (
          <a href={urlQ.data} target="_blank" rel="noreferrer" className="block">
            <img src={urlQ.data} alt={attachment.file_name} className="h-32 w-full object-cover" />
          </a>
        ) : (
          <Skeleton className="h-32 w-full rounded-none" />
        )
      ) : null}
      <div className="flex items-center gap-2 px-3 py-2">
        {!isImage && (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary/60 text-muted-foreground">
            <FileIcon className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-foreground">{attachment.file_name}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {personLabel(attachment.uploader, 'Someone')} · {formatRelative(attachment.created_at)}
          </p>
        </div>
        {urlQ.data && (
          <a
            href={urlQ.data}
            target="_blank"
            rel="noreferrer"
            download={attachment.file_name}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label={`Download ${attachment.file_name}`}
          >
            <Download className="h-4 w-4" />
          </a>
        )}
        {canDelete && (
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(`Remove "${attachment.file_name}"?`)) return;
              remove.mutate(attachment, { onError: (err) => toast.error(describeTicketError(err)) });
            }}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-destructive"
            aria-label={`Remove ${attachment.file_name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Activity — events and comments in one thread, plus the composer
// ---------------------------------------------------------------------------

type ActivityItem =
  | { kind: 'event'; at: string; event: TicketEventWithActor }
  | { kind: 'comment'; at: string; comment: TicketCommentWithAuthor };

function ActivitySection({
  ticket,
  people,
  canWork,
  closed,
}: {
  ticket: TicketWithContext;
  people: PeoplePickerProfile[];
  canWork: boolean;
  closed: boolean;
}) {
  const { profile } = useAuth();
  const commentsQ = useTicketComments(ticket.id);
  const eventsQ = useTicketEvents(ticket.id);
  const create = useCreateTicketComment();
  const remove = useDeleteTicketComment();
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [internal, setInternal] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const items = useMemo<ActivityItem[]>(() => {
    const out: ActivityItem[] = [];
    for (const e of eventsQ.data ?? []) {
      if (e.kind === 'attachment_added') continue;
      out.push({ kind: 'event', at: e.created_at, event: e });
    }
    for (const c of commentsQ.data ?? []) out.push({ kind: 'comment', at: c.created_at, comment: c });
    out.sort((a, b) => a.at.localeCompare(b.at));
    return out;
  }, [eventsQ.data, commentsQ.data]);

  const visible = showAll || items.length <= 12 ? items : items.slice(items.length - 12);

  const send = () => {
    const text = body.trim();
    if (!text) return;
    create.mutate(
      { ticketId: ticket.id, body: text, isInternal: internal, mentionIds: mentions },
      {
        onSuccess: () => {
          setBody('');
          setMentions([]);
        },
        onError: (err) => toast.error(describeTicketError(err)),
      },
    );
  };

  return (
    <section className="space-y-3">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Activity</h3>

      {eventsQ.isLoading || commentsQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <ol className="space-y-3">
          {items.length > visible.length && (
            <li>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Show {items.length - visible.length} earlier
              </button>
            </li>
          )}
          {visible.map((item) =>
            item.kind === 'event' ? (
              <EventRow key={item.event.id} event={item.event} />
            ) : (
              <CommentRow
                key={item.comment.id}
                comment={item.comment}
                mine={item.comment.author_id === profile?.id}
                onDelete={() =>
                  remove.mutate(
                    { id: item.comment.id, ticketId: ticket.id },
                    { onError: (err) => toast.error(describeTicketError(err)) },
                  )
                }
              />
            ),
          )}
        </ol>
      )}

      <div className={cn('rounded-lg border p-3', internal ? 'border-fresh-salmon/60 bg-fresh-salmon/10' : 'border-border bg-background')}>
        <MentionTextarea
          value={body}
          onChange={setBody}
          onMentionsChange={setMentions}
          candidates={people}
          placeholder={
            closed
              ? 'Add a note (the ticket is closed, but the thread stays open)…'
              : internal
                ? 'Internal note — the requester will not see this…'
                : 'Reply… (@ to tag someone)'
          }
          className="min-h-[72px] border-0 bg-transparent px-0 py-0 shadow-none focus-visible:ring-0"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          {canWork ? (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={internal} onCheckedChange={setInternal} aria-label="Internal note" />
              <Lock className="h-3 w-3" />
              Internal note
            </label>
          ) : (
            <span />
          )}
          <Button size="sm" onClick={send} disabled={create.isPending || body.trim().length === 0}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {internal ? 'Add note' : 'Reply'}
          </Button>
        </div>
      </div>
    </section>
  );
}

function EventRow({ event }: { event: TicketEventWithActor }) {
  const meta = event.kind === 'status_changed' && event.to_value && event.to_value in TICKET_STATUS_META
    ? TICKET_STATUS_META[event.to_value as TicketStatus]
    : null;
  return (
    <li className="flex items-start gap-3 text-xs text-muted-foreground">
      <span
        aria-hidden
        className={cn('mt-1.5 ml-2.5 h-2 w-2 shrink-0 rounded-full', meta ? meta.dot : 'bg-border')}
      />
      <p className="min-w-0 flex-1 leading-5">
        <span className="font-medium text-foreground/80">{personLabel(event.actor, 'Atlas')}</span>{' '}
        {describeTicketEvent(event)}
        {event.kind === 'status_changed' &&
          typeof event.meta === 'object' &&
          event.meta &&
          !Array.isArray(event.meta) &&
          typeof (event.meta as { resolution?: unknown }).resolution === 'string' && (
            <span className="block text-foreground/80">“{(event.meta as { resolution: string }).resolution}”</span>
          )}
        <span className="ml-2" title={formatDateTime(event.created_at)}>
          {formatRelative(event.created_at)}
        </span>
      </p>
    </li>
  );
}

function CommentRow({
  comment,
  mine,
  onDelete,
}: {
  comment: TicketCommentWithAuthor;
  mine: boolean;
  onDelete: () => void;
}) {
  const author: TicketPerson | null = comment.author;
  return (
    <li className="group flex items-start gap-3">
      <Avatar className="mt-0.5 h-7 w-7 shrink-0">
        {author?.avatar_url && <AvatarImage src={author.avatar_url} alt="" />}
        <AvatarFallback className="text-[10px]">{author ? initialsOf(author) : '?'}</AvatarFallback>
      </Avatar>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg border px-3 py-2',
          comment.is_internal ? 'border-fresh-salmon/60 bg-fresh-salmon/10' : 'border-border bg-card',
        )}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{personLabel(author, 'Former teammate')}</span>
          <span title={formatDateTime(comment.created_at)}>{formatRelative(comment.created_at)}</span>
          {comment.is_internal && (
            <span className="inline-flex items-center gap-1 rounded-full bg-fresh-salmon/30 px-1.5 py-0.5 text-[10px] font-medium text-fresh-salmon-dark">
              <Lock className="h-2.5 w-2.5" /> Internal
            </span>
          )}
          {mine && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Delete this comment?')) onDelete();
              }}
              className="ml-auto hidden text-muted-foreground hover:text-destructive group-hover:inline-flex"
              aria-label="Delete comment"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{comment.body}</p>
      </div>
    </li>
  );
}
